import React, { useCallback, useState } from "react";
import { Upload, X, Image, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const LogoUploader = ({ 
  value, 
  onChange, 
  language = "it",
  className = ""
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleUpload = useCallback(async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
    if (!allowedTypes.includes(file.type)) {
      toast.error(language === "it" 
        ? "Tipo file non supportato. Usa JPG, PNG, GIF, WebP o SVG" 
        : "File type not supported. Use JPG, PNG, GIF, WebP or SVG"
      );
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(language === "it" ? "File troppo grande. Max 5MB" : "File too large. Max 5MB");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload/logo`, formData, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      
      onChange(response.data.url);
      toast.success(language === "it" ? "Logo caricato!" : "Logo uploaded!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore nel caricamento" : "Upload error"));
    } finally {
      setUploading(false);
    }
  }, [onChange, language]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleUpload(file);
    }
  }, [handleUpload]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  }, [handleUpload]);

  const handleRemove = useCallback(() => {
    onChange("");
  }, [onChange]);

  return (
    <div className={className}>
      <label className="block text-sm font-medium mb-1.5 flex items-center gap-2">
        <Image className="w-4 h-4" />
        Logo
      </label>
      
      {value ? (
        // Preview mode
        <div className="relative p-4 rounded-lg border-2 border-dashed border-border bg-muted/30">
          <div className="flex items-center gap-4">
            <img 
              src={value} 
              alt="Logo preview" 
              className="h-16 w-auto max-w-[200px] object-contain rounded"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {language === "it" ? "Logo caricato" : "Logo uploaded"}
              </p>
              <p className="text-xs text-muted-foreground truncate">{value}</p>
            </div>
            <button
              type="button"
              onClick={handleRemove}
              className="p-2 rounded-full hover:bg-destructive/10 text-destructive transition-colors"
              data-testid="remove-logo-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      ) : (
        // Upload mode
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            relative p-8 rounded-lg border-2 border-dashed transition-colors cursor-pointer
            ${dragOver 
              ? "border-accent bg-accent/5" 
              : "border-border hover:border-accent/50 hover:bg-muted/30"
            }
          `}
        >
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
            onChange={handleFileSelect}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={uploading}
            data-testid="logo-file-input"
          />
          
          <div className="text-center">
            {uploading ? (
              <>
                <Loader2 className="w-10 h-10 mx-auto mb-3 text-accent animate-spin" />
                <p className="text-sm font-medium">
                  {language === "it" ? "Caricamento..." : "Uploading..."}
                </p>
              </>
            ) : (
              <>
                <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm font-medium">
                  {language === "it" 
                    ? "Trascina qui il logo o clicca per selezionare" 
                    : "Drag logo here or click to select"
                  }
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  JPG, PNG, GIF, WebP, SVG • Max 5MB
                </p>
                <p className="text-xs text-muted-foreground">
                  {language === "it" 
                    ? "Consigliato: 200x50 px, sfondo trasparente" 
                    : "Recommended: 200x50 px, transparent background"
                  }
                </p>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* URL fallback input */}
      {!value && (
        <div className="mt-3">
          <p className="text-xs text-muted-foreground mb-1">
            {language === "it" ? "Oppure inserisci un URL:" : "Or enter a URL:"}
          </p>
          <input
            type="url"
            placeholder="https://esempio.com/logo.png"
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-accent/50"
            data-testid="logo-url-input"
          />
        </div>
      )}
    </div>
  );
};

export default LogoUploader;
